import React, { useState, useEffect } from "react";
import { BrowserRouter as Router, Route, Routes, Navigate, useNavigate, useLocation } from "react-router-dom";
import "./App.css";

import "./i18n/config";
import { LanguageProvider } from "./contexts/LanguageContext";
import Nav from "./components/Nav";
import LanguageSwitcher from "./components/LanguageSwitcher";
import DonationsButton from "./components/DonationsButton";
import SupportButton from "./components/SupportButton";
import Donations from "./components/Donations";
import Footer from "./components/Footer";
import ContentContainer from "./components/ContentContainer";
import Home from "./pages/Home";
import PathBuilder from "./pages/PathBuilder";
import What from "./pages/What";
import Why from "./pages/Why";
import Thanks from "./pages/Thanks";
import Who from "./pages/Who";
import NonDev from "./pages/NonDev";
import StartingDev from "./pages/StartingDev";
import Expert from "./pages/Expert";
import Satochip from "./pages/Satochip";
import Satodime from "./pages/Satodime";
import When from "./pages/When";
import Seedkeeper from "./pages/Seedkeeper";
import Utils from "./pages/Utils";

// Component to handle GitHub Pages 404.html redirect
function GitHubPagesRedirect() {
  const location = useLocation();
  const navigate = useNavigate();
  
  useEffect(() => {
    // Handle GitHub Pages 404.html redirect format
    // The 404.html converts paths like /path to /?/path
    // Format is: ?/path or ?/path&other=params
    if (location.search && location.search.startsWith('?/')) {
      // Extract the path from ?/path format
      let path = location.search.slice(2); // Remove '?/'
      
      // Handle additional query parameters (if any)
      const ampersandIndex = path.indexOf('&');
      if (ampersandIndex !== -1) {
        path = path.slice(0, ampersandIndex);
      }
      
      // Decode the path (replace ~and~ with &)
      const decodedPath = path.replace(/~and~/g, '&');
      
      // Ensure path starts with /
      const finalPath = decodedPath.startsWith('/') ? decodedPath : '/' + decodedPath;
      
      // Navigate to the path using React Router
      navigate(finalPath, { replace: true });
    }
  }, [location, navigate]);
  
  return null;
}

function AppContent() {
  const navigate = useNavigate();
  const [isDonationsOpen, setIsDonationsOpen] = useState(false);
  
  return (
    <div className="App">
      <GitHubPagesRedirect />
      <header className="app-header">
        <ContentContainer className="header-content">
        <div className="logo-container" onClick={() => navigate("/")} style={{ cursor: "pointer" }}>
          <img 
            src={`${process.env.PUBLIC_URL}/assets/images/logo.png`}
            alt="SeedSigner Satochip Workshop Logo"
            className="app-logo"
          />
        </div>
        <div className="header-right">
          <Nav />
        </div>
          <div className="header-actions">
            <SupportButton
              ariaLabel="Get Support"
              title="Get Support on Telegram"
            />
            <DonationsButton
              onClick={() => setIsDonationsOpen(true)}
              ariaLabel="Donations"
              title="Donations"
            />
        <LanguageSwitcher />
          </div>
        </ContentContainer>
      </header>
      <Routes>
        <Route path="/how" element={<PathBuilder />} />
        <Route path="/what" element={<What />} />
        <Route path="/get-started" element={<Navigate to="/how" replace />} />
        <Route path="/instructions/non-dev" element={<NonDev />} />
        <Route path="/instructions/starting-dev" element={<StartingDev />} />
        <Route path="/instructions/expert" element={<Expert />} />
        <Route path="/why" element={<Why />} />
        <Route path="/who" element={<Who />} />
        <Route path="/when" element={<When />} />
        <Route path="/thanks" element={<Thanks />} />
        <Route path="/instructions/satochip" element={<Satochip />} />
        <Route path="/instructions/satodime" element={<Satodime />} />
        <Route path="/instructions/seedkeeper" element={<Seedkeeper />} />
        <Route path="/instructions/utils" element={<Utils />} />
        <Route path="/*" element={<Home />} />
      </Routes>
      <Footer />
      <Donations isOpen={isDonationsOpen} onClose={() => setIsDonationsOpen(false)} />
    </div>
  );
}

function App() {
  // Get basename from environment or default to production
  const getBasename = () => {
    if (process.env.NODE_ENV === "development" || process.env.NODE_ENV === "test") return "";
    // Check if we're on staging by looking at the homepage in package.json
    // or use environment variable
    const isStaging = process.env.REACT_APP_STAGING === "true" || 
                      window.location.hostname.includes("staging");
    if (isStaging) return "/seedsigner-satochip-workshop-staging";
    return "/seedsigner-satochip-workshop";
  };

  const basename = getBasename();
  
  return (
    <LanguageProvider>
      <Router basename={basename}>
        <AppContent />
      </Router>
    </LanguageProvider>
  );
}

export default App;
